/* import __COLOCATED_TEMPLATE__ from './sankey.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import type { Options, Point, SeriesOptionsType, SeriesSankeyOptions } from 'highcharts';
import { use } from 'ember-resources/util/function-resource';
import { FinInsightsSankeyConfig } from 'embercom/components/reporting/automation/ai-insights/fin-insights/sankey-config';
import PALETTE from '@intercom/pulse/lib/palette';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import type { Job } from 'embercom/components/reporting/automation/ai-insights/fin-insights/jobs';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';

type ExtendedSeriesSankeyNodesOptionsObject =
  | Highcharts.SeriesSankeyNodesOptionsObject
  | { selected?: boolean };
type ExtendedSeriesSankeyOptions =
  | SeriesSankeyOptions
  | { nodes: Array<ExtendedSeriesSankeyNodesOptionsObject> };

export const SANKEY_COLORS = {
  queryType: PALETTE['vis-violet-30'],
  agentOutcome: PALETTE['vis-pink-30'],
  csat: PALETTE['vis-salmon-50'],
  default: PALETTE['neutral-container-emphasis'],
};

const FILTER_STATE_TO_SELECTED_NODES: Record<string, string[]> = {
  infoToRoutedToTeammate: ['queryTypeInformational', 'finRoutedToTeam'],
  infoToTeammate: ['queryTypeInformational', 'teammateOnly'],
  personalizedToRoutedToTeam: ['queryTypePersonalized', 'finRoutedToTeam'],
  taskBasedToRoutedToTeam: ['queryTypeActions', 'finRoutedToTeam'],
  resolvedByFinToPositiveCSAT: ['finResolved', 'positiveCSAT'],
  resolvedByFinToNegativeCSAT: ['finResolved', 'negativeCSAT'],
};

type OptionsWithSankey = Options & { plotOptions: { sankey: { borderRadius: number } } };

function safelyAdjustLayout(series: any, adjust: (series: any) => void) {
  if (!series.adjustingLayout) {
    series.adjustingLayout = true;
    // because layout adjustments can trigger a redraw, we need to prevent infinite recursion
    adjust(series);
    series.redraw();
    series.adjustingLayout = false;
  }
}

function adjustNodeLayout(event: Highcharts.PointerEventObject) {
  let chart = event.target as any;
  let series = chart.series[0];

  safelyAdjustLayout(series, () => {
    // verticallyAlignTeammateAndClosedNodes(series);
    // it's important to adjust the padding last because the other changes
    // can influence the data label positions
    // adjustPaddingIfDataLabelsOverlap(series);
  });
}

interface Signature {
  reportState: ReportState;
  selectedJob: Job;
}

interface SankeyPoint extends Point {
  title?: string;
  tooltipBody?: string;
}

export default class Sankey extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: $TSFixMe;

  @tracked tooltipOpenerElement: HTMLElement | undefined = undefined;
  @tracked hoveredPoint: SankeyPoint | null = null;
  @tracked hoveredTooltipHeader: string | undefined = undefined;
  @tracked hoveredTooltipBody: string | undefined = undefined;

  @use dataResource = staticDataResource(this, {
    serializedChart: () => this.sankeyChartDefinition,
    reportState: () => this.args.reportState,
  });

  get highchartsSeries() {
    return [this.sankeySeries] as SeriesOptionsType[];
  }

  get sankeyChartDefinition() {
    return {
      chart_series: FinInsightsSankeyConfig.getChartSeriesWithLabels(this.intl),
      view_by: null,
    };
  }

  @action
  setTooltipOpenerElement(element: HTMLElement) {
    if (this.hoveredPoint?.tooltipBody) {
      this.tooltipOpenerElement = element;
      this.hoveredTooltipHeader = this.hoveredPoint.title;
      this.hoveredTooltipBody = this.hoveredPoint.tooltipBody;
    }
  }

  get highchartsOptions(): OptionsWithSankey {
    let component = this;
    // a sankey chart doesn't have axes, or a legend
    // we are adding some dummy series so we can show stuff in the chart legend
    // but having other series will enable the axes, so we need to hide them
    return {
      xAxis: {
        visible: false,
      },
      yAxis: {
        visible: false,
      },
      tooltip: {
        enabled: false,
        style: {
          zIndex: 100, // Higher than dataLabels zIndex
        },
        useHTML: true,
        backgroundColor: undefined,
        shadow: false,
        borderWidth: 0,
        borderColor: undefined,
        borderRadius: 0,
        padding: 0,
      },
      chart: {
        marginLeft: 0,
        marginRight: 0,
        marginTop: 0,
        marginBottom: 0,
        animation: false, // disables animations on redraw (but not initial draw)
        events: {
          redraw: adjustNodeLayout.bind(this),
        },
      },
      plotOptions: {
        sankey: {
          point: {
            events: {
              mouseOver(this: Point, event: PointerEvent) {
                component.hoveredPoint = this;
                component.setTooltipOpenerElement((event.target as any).graphic.element);
                return false;
              },
              mouseOut(this: Point, _event: PointerEvent) {
                component.tooltipOpenerElement = undefined;
                return false;
              },
            },
          },
          nodeWidth: 16,
          borderRadius: 4,
          nodePadding: 8,
          nodeAlignment: 'center',
          linkOpacity: 1,
          states: {
            inactive: {
              opacity: 1,
              linkOpacity: 1.0,
            },
            select: {
              color: SANKEY_COLORS.default,
            },
          },
          dataLabels: {
            padding: 0,
            color: 'black',
            useHTML: true,
            // crop: false & overflow: allow tell highcharts that it's okay to render the labels in the margins
            crop: false,
            overflow: 'allow',
            allowOverlap: false, // we detect if labels are hidden and increase padding
            enabled: true,
            zIndex: 10,
            // see https://www.highcharts.com/docs/chart-concepts/templating
            nodeFormat: `
              <div>
                <div class="text-caption  text-default font-normal leading-4">
                  {point.title}
                  <span class="font-mono text-muted ml-2">
                    {point.fullValue:,.0f}
                  </span>
                </div>
              </div>
            `,
          },
          levels: [
            {
              level: 0,
              dataLabels: {
                x: 30,
                align: 'left',
              },
            },
            {
              level: 1,
              dataLabels: {
                x: -30,
                align: 'right',
              },
            },
          ],
        },
      },
    };
  }

  get metrics() {
    return FinInsightsSankeyConfig.getMetricsFromChartDataRequest(this.dataResource.chartData);
  }

  get hasData() {
    let topOfFunnel =
      this.metrics.queryTypeInformational ||
      this.metrics.queryTypePersonalized ||
      this.metrics.queryTypeComplex ||
      this.metrics.queryTypeActions;
    return !!topOfFunnel;
  }

  get queryTypeNodes() {
    return [
      {
        id: 'queryTypeInformational',
        title: this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.sankey.nodes.informational',
        ),
        color: PALETTE[this.args.selectedJob.sankeyColour as keyof typeof PALETTE],
        selected: this.nodeSelected('queryTypeInformational'),
        fullValue: this.metrics.queryTypeInformational,
        tooltipBody: this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.sankey.nodes.informational-tooltip-body',
        ),
      },
      {
        id: 'queryTypePersonalized',
        title: this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.sankey.nodes.personalized',
        ),
        color: PALETTE[this.args.selectedJob.sankeyColour as keyof typeof PALETTE],
        selected: this.nodeSelected('queryTypePersonalized'),
        fullValue: this.metrics.queryTypePersonalized,
        tooltipBody: this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.sankey.nodes.personalized-tooltip-body',
        ),
      },
      {
        id: 'queryTypeActions',
        title: this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.sankey.nodes.actions',
        ),
        color: PALETTE[this.args.selectedJob.sankeyColour as keyof typeof PALETTE],
        selected: this.nodeSelected('queryTypeActions'),
        fullValue: this.metrics.queryTypeActions,
        tooltipBody: this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.sankey.nodes.actions-tooltip-body',
        ),
      },
      {
        id: 'queryTypeComplex',
        title: this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.sankey.nodes.complex',
        ),
        color: PALETTE[this.args.selectedJob.sankeyColour as keyof typeof PALETTE],
        selected: this.nodeSelected('queryTypeComplex'),
        fullValue: this.metrics.queryTypeComplex,
        tooltipBody: this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.sankey.nodes.complex-tooltip-body',
        ),
      },
    ];
  }

  get handlingNodes() {
    return [
      {
        id: 'finResolved',
        title: this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.sankey.nodes.finResolved',
        ),
        color: PALETTE[this.args.selectedJob.sankeyColour as keyof typeof PALETTE],
        selected: this.nodeSelected('finResolved'),
        fullValue: this.metrics.finResolved,
      },
      {
        id: 'finRoutedToTeam',
        title: this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.sankey.nodes.finRoutedToTeam',
        ),
        color: PALETTE[this.args.selectedJob.sankeyColour as keyof typeof PALETTE],
        selected: this.nodeSelected('finRoutedToTeam'),
        fullValue: this.metrics.finRoutedToTeam,
      },
      {
        id: 'teammateOnly',
        title: this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.sankey.nodes.teammateOnly',
        ),
        color: PALETTE[this.args.selectedJob.sankeyColour as keyof typeof PALETTE],
        selected: this.nodeSelected('teammateOnly'),
        fullValue: this.metrics.teammateOnly,
      },
    ];
  }

  get csatNodes() {
    return [
      {
        id: 'positiveCSAT',
        title: this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.sankey.nodes.positiveCX',
        ),
        color: PALETTE[this.args.selectedJob.sankeyColour as keyof typeof PALETTE],
        selected: this.nodeSelected('positiveCSAT'),
        fullValue: this.metrics.positiveCSAT,
      },
      {
        id: 'neutralCSAT',
        title: this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.sankey.nodes.neutralCX',
        ),
        color: PALETTE[this.args.selectedJob.sankeyColour as keyof typeof PALETTE],
        selected: this.nodeSelected('neutralCSAT'),
        fullValue: this.metrics.neutralCSAT,
      },
      {
        id: 'negativeCSAT',
        title: this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.sankey.nodes.negativeCX',
        ),
        color: PALETTE[this.args.selectedJob.sankeyColour as keyof typeof PALETTE],
        selected: this.nodeSelected('negativeCSAT'),
        fullValue: this.metrics.negativeCSAT,
      },
    ];
  }

  get queryTypeToHandlingNodes() {
    return [
      {
        from: 'queryTypeInformational',
        to: 'finResolved',
        weight: this.metrics.informationalAndFinResolved,
        selected: true,
        className: 'sankey-link-active',
      },
      {
        from: 'queryTypeInformational',
        to: 'finRoutedToTeam',
        weight: this.metrics.informationalAndFinRoutedToTeam,
        selected: this.args.selectedJob.sankey.highlightedLinks !== 'infoToRoutedToTeammate',
        className:
          this.args.selectedJob.sankey.highlightedLinks === 'infoToRoutedToTeammate'
            ? 'sankey-link-inactive'
            : 'sankey-link-active',
      },
      {
        from: 'queryTypeInformational',
        to: 'teammateOnly',
        weight: this.metrics.informationalAndTeammateOnly,
        selected: this.args.selectedJob.sankey.highlightedLinks !== 'infoToTeammate',
        className:
          this.args.selectedJob.sankey.highlightedLinks === 'infoToTeammate'
            ? 'sankey-link-inactive'
            : 'sankey-link-active',
      },
      {
        from: 'queryTypePersonalized',
        to: 'finResolved',
        weight: this.metrics.personalizedAndFinResolved,
        selected: true,
        className: 'sankey-link-active',
      },
      {
        from: 'queryTypePersonalized',
        to: 'finRoutedToTeam',
        weight: this.metrics.personalizedAndFinRoutedToTeam,
        selected: this.args.selectedJob.sankey.highlightedLinks !== 'personalizedToRoutedToTeam',
        className:
          this.args.selectedJob.sankey.highlightedLinks === 'personalizedToRoutedToTeam'
            ? 'sankey-link-inactive'
            : 'sankey-link-active',
      },
      {
        from: 'queryTypePersonalized',
        to: 'teammateOnly',
        weight: this.metrics.personalizedAndTeammateOnly,
        selected: true,
        className: 'sankey-link-active',
      },
      {
        from: 'queryTypeActions',
        to: 'finResolved',
        weight: this.metrics.actionsAndFinResolved,
        selected: true,
        className: 'sankey-link-active',
      },
      {
        from: 'queryTypeActions',
        to: 'finRoutedToTeam',
        weight: this.metrics.actionsAndFinRoutedToTeam,
        selected: this.args.selectedJob.sankey.highlightedLinks !== 'taskBasedToRoutedToTeam',
        className:
          this.args.selectedJob.sankey.highlightedLinks === 'taskBasedToRoutedToTeam'
            ? 'sankey-link-inactive'
            : 'sankey-link-active',
      },
      {
        from: 'queryTypeActions',
        to: 'teammateOnly',
        weight: this.metrics.actionsAndTeammateOnly,
        selected: true,
        className: 'sankey-link-active',
      },
      {
        from: 'queryTypeComplex',
        to: 'finResolved',
        weight: this.metrics.complexAndFinResolved,
        selected: true,
        className: 'sankey-link-active',
      },
      {
        from: 'queryTypeComplex',
        to: 'finRoutedToTeam',
        weight: this.metrics.complexAndFinRoutedToTeam,
        selected: true,
        className: 'sankey-link-active',
      },
      {
        from: 'queryTypeComplex',
        to: 'teammateOnly',
        weight: this.metrics.complexAndTeammateOnly,
        selected: true,
        className: 'sankey-link-active',
      },
    ];
  }

  get handlingToCsatNodes() {
    return [
      {
        from: 'finResolved',
        to: 'positiveCSAT',
        weight: this.metrics.finResolvedAndPositiveCSAT,
        selected: this.args.selectedJob.sankey.highlightedLinks !== 'resolvedByFinToPositiveCSAT',
        className:
          this.args.selectedJob.sankey.highlightedLinks === 'resolvedByFinToPositiveCSAT'
            ? 'sankey-link-inactive'
            : 'sankey-link-active',
      },
      {
        from: 'finResolved',
        to: 'neutralCSAT',
        weight: this.metrics.finResolvedAndNeutralCSAT,
        selected: true,
        className: 'sankey-link-active',
      },
      {
        from: 'finResolved',
        to: 'negativeCSAT',
        weight: this.metrics.finResolvedAndNegativeCSAT,
        selected: this.args.selectedJob.sankey.highlightedLinks !== 'resolvedByFinToNegativeCSAT',
        className:
          this.args.selectedJob.sankey.highlightedLinks === 'resolvedByFinToNegativeCSAT'
            ? 'sankey-link-inactive'
            : 'sankey-link-active',
      },
      {
        from: 'finRoutedToTeam',
        to: 'positiveCSAT',
        weight: this.metrics.finRoutedToTeamAndPositiveCSAT,
        selected: true,
        className: 'sankey-link-active',
      },
      {
        from: 'finRoutedToTeam',
        to: 'neutralCSAT',
        weight: this.metrics.finRoutedToTeamAndNeutralCSAT,
        selected: true,
        className: 'sankey-link-active',
      },
      {
        from: 'finRoutedToTeam',
        to: 'negativeCSAT',
        weight: this.metrics.finRoutedToTeamAndNegativeCSAT,
        selected: true,
        className: 'sankey-link-active',
      },
      {
        from: 'teammateOnly',
        to: 'positiveCSAT',
        weight: this.metrics.teammateOnlyAndPositiveCSAT,
        selected: true,
        className: 'sankey-link-active',
      },
      {
        from: 'teammateOnly',
        to: 'neutralCSAT',
        weight: this.metrics.teammateOnlyAndNeutralCSAT,
        selected: true,
        className: 'sankey-link-active',
      },
      {
        from: 'teammateOnly',
        to: 'negativeCSAT',
        weight: this.metrics.teammateOnlyAndNegativeCSAT,
        selected: true,
        className: 'sankey-link-active',
      },
    ];
  }

  nodeSelected(nodeId: string) {
    let highlightedLinks = this.args.selectedJob.sankey.highlightedLinks;
    let selectedNodes = FILTER_STATE_TO_SELECTED_NODES[highlightedLinks];
    return selectedNodes ? !selectedNodes.includes(nodeId) : false;
  }

  // TODO: localize these when the strings are finalized
  /* eslint-disable @intercom/intercom/no-bare-strings */
  get sankeySeries(): ExtendedSeriesSankeyOptions {
    let nodes =
      this.args.selectedJob.sankey.type === 'queryTypeToRes'
        ? [...this.queryTypeNodes, ...this.handlingNodes]
        : [...this.handlingNodes, ...this.csatNodes];
    let data =
      this.args.selectedJob.sankey.type === 'queryTypeToRes'
        ? this.queryTypeToHandlingNodes
        : this.handlingToCsatNodes;
    return {
      type: 'sankey',
      linkColorMode: 'gradient',
      animation: false,
      custom: {
        // this is used in the data label template (plotOptions.sankey.nodeFormat)
        total: this.metrics.conversations,
      },
      nodes,
      data,
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::FinInsights::Sankey': typeof Sankey;
    'reporting/automation/ai-insights/fin-insights/sankey': typeof Sankey;
  }
}
